import { useEffect } from 'react';

const KEYBOARD_KEYS = {
  ROTATION: [
    'w',
    'a',
    's',
    'd',
    'arrowup',
    'arrowdown',
    'arrowleft',
    'arrowright',
  ],
  ZOOM: ['+', '=', '-', '_'],
};

const DIRECTION_MAP: Record<string, string> = {
  w: 'up',
  s: 'down',
  a: 'left',
  d: 'right',
  arrowup: 'up',
  arrowdown: 'down',
  arrowleft: 'left',
  arrowright: 'right',
};

const useCanvasAccessibility = (canvasId: string) => {
  useEffect(() => {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

    if (!canvas) {
      console.warn(`Canvas with id "${canvasId}" not found`);

      return;
    }

    // Set accessibility attributes
    canvas.tabIndex = 0;
    {
      /* @todo Add i18n for aria-label */
    }
    canvas.setAttribute('aria-label', 'Interactive 3D View');
    canvas.setAttribute('aria-live', 'polite');
    canvas.setAttribute('aria-atomic', 'true');

    const announceAction = (action: string, direction?: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const announcement = direction ? `${action} ${direction}` : action;
      // TODO: Implement announcement when needed
    };

    const focusCanvas = () => {
      canvas.focus();
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        focusCanvas();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const isShiftPressed = event.shiftKey;
      const direction = DIRECTION_MAP[key];

      // Rotation: WASD or Arrow keys (without Shift)
      if (KEYBOARD_KEYS.ROTATION.includes(key) && !isShiftPressed) {
        event.preventDefault();
        announceAction('Rotating', direction);

        return;
      }

      // Panning: Shift + WASD or Arrow keys
      if (KEYBOARD_KEYS.ROTATION.includes(key) && isShiftPressed) {
        event.preventDefault();
        announceAction('Panning', direction);

        return;
      }

      // Zooming: +/- keys
      if (KEYBOARD_KEYS.ZOOM.includes(key)) {
        event.preventDefault();
        const zoomDirection = key === '+' || key === '=' ? 'in' : 'out';

        announceAction(`Zooming ${zoomDirection}`);

        return;
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    canvas.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
      canvas.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvasId]);
};

export default useCanvasAccessibility;
